import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import './Auth.css';

function Login() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const rawRedirect   = searchParams.get('redirect')
    ? decodeURIComponent(searchParams.get('redirect'))
    : '/dashboard';
  // Prevent open-redirect attacks — only allow internal paths
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/dashboard';

  // ── Login form state ─────────────────────────────────────
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors]       = useState({});
  const [authError, setAuthError] = useState('');
  const [loading, setLoading]     = useState(false);

  // ── Forgot password state ────────────────────────────────
  const [forgotMode, setForgotMode]       = useState(false);
  const [forgotEmail, setForgotEmail]     = useState('');
  const [forgotError, setForgotError]     = useState('');
  const [forgotSent, setForgotSent]       = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  function validate() {
    const newErrors = {};
    if (!email.trim())
      newErrors.email = 'Please enter your email address.';
    if (!password)
      newErrors.password = 'Please enter your password.';
    return newErrors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setAuthError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      setAuthError(error.message);
      return;
    }

    navigate(redirectTo);
  }

  async function handleForgotSubmit(e) {
    e.preventDefault();
    setForgotError('');

    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }

    setForgotLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: window.location.origin + '/reset-password',
    });
    setForgotLoading(false);

    if (error) {
      setForgotError(error.message);
      return;
    }

    setForgotSent(true);
  }

  function enterForgotMode() {
    setForgotMode(true);
    setForgotEmail(email); // pre-fill if they already typed their email
    setForgotError('');
    setForgotSent(false);
  }

  function exitForgotMode() {
    setForgotMode(false);
    setForgotError('');
    setForgotSent(false);
  }

  // ── Forgot password view ─────────────────────────────────
  if (forgotMode) {
    return (
      <div className="auth-page">
        <div className="auth-card">

          <Link to="/" className="auth-logo">
            Rank<span>TT</span>
          </Link>

          <h1 className="auth-title">Reset password</h1>
          <p className="auth-subtitle">
            Enter your email and we'll send you a link to create a new password.
          </p>

          {forgotSent ? (
            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <p style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
                Reset link sent!
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>
                Check your email for a link to reset your password. It may take a minute to arrive.
              </p>
              <button className="auth-link" onClick={exitForgotMode} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                &larr; Back to log in
              </button>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleForgotSubmit} noValidate>

              <FormField label="Email address" htmlFor="forgot-email" error={forgotError}>
                <Input
                  id="forgot-email"
                  name="email"
                  type="email"
                  placeholder="jane@example.com"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  error={forgotError}
                />
              </FormField>

              {forgotError && <p className="auth-error">{forgotError}</p>}

              <Button type="submit" fullWidth size="lg" disabled={forgotLoading}>
                {forgotLoading ? 'Sending…' : 'Send reset link'}
              </Button>

              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="auth-link"
                  onClick={exitForgotMode}
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  &larr; Back to log in
                </button>
              </div>

            </form>
          )}

        </div>
      </div>
    );
  }

  // ── Normal login view ────────────────────────────────────
  return (
    <div className="auth-page">
      <div className="auth-card">

        <Link to="/" className="auth-logo">
          Rank<span>TT</span>
        </Link>

        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">Log in to your RankTT account.</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>

          <FormField label="Email address" htmlFor="email" error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="jane@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              error={errors.email}
            />
          </FormField>

          <FormField label="Password" htmlFor="password" error={errors.password}>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              error={errors.password}
            />
          </FormField>

          <div className="auth-forgot">
            <button
              type="button"
              className="auth-link"
              onClick={enterForgotMode}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Forgot password?
            </button>
          </div>

          {authError && <p className="auth-error">{authError}</p>}

          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Logging in…' : 'Log in'}
          </Button>

        </form>

        <p className="auth-switch">
          Don't have an account?{' '}
          <Link to="/signup" className="auth-link">Sign up free &rarr;</Link>
        </p>

      </div>
    </div>
  );
}

export default Login;
